import React, { Suspense, lazy, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { GridColDef } from "@mui/x-data-grid";
import { formatAdjustedProductType } from "../../../utils/enumFormatter";
import { formatCurrency } from "../../../utils/namesFormater";
import StyledDataGrid from "../../../shared/StyledDataGrid";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import InsuranceProductDrawer from "../Insurance/InsuranceProductDrawer";
import InvestmentPortfolioDrawer from "../../Investment/InvestmentPortfolioDrawer";
import { ManualMimshakData, ManualProductType } from "../../../interfaces/DTO/manualMimshakData";
import { Product } from "../../../interfaces/DTO/mislaka_xml_models";
import {
  adjustedProductTypeColumnFullColor,
  currencyColumn,
  handlerCodeUniversalColumn,
  isoDateColumn,
  policyNumberWithPathTooltipColumn,
  productStatusChipsColumn,
  managementFeeColumn,
} from "../../../shared/UIParts/columnsFormatter";
import { useContactFirstNameColumn } from "../../../hooks/useContactNameColumn";
import { PensionFeeExpenseType } from "../../../interfaces/DTO/mislaka_xml_pension_models";
import { PolicyHistoryKey } from "../../../hooks/usePolicyHistory";
import { buildPolicyRow, depositsWithdrawalsColumn, productJoinDateColumn, transfersColumn } from "./policyTableColumns";

const PolicyHistoryDrawer = lazy(() => import("./PolicyHistoryDrawer"));

const isInvestmentPortfolio = (row: { product_type?: unknown; raw_product_type?: unknown }) =>
  (row.raw_product_type ?? row.product_type) === ManualProductType.INVESTMENT_PORTFOLIO;

interface PolicyManagementTableProps {
  data: MimshakData[];
  viewedType: "USER" | "FAMILY";
  selectedPeriodType?: "REGULAR" | "PRE_ADVICE";
  handlerFilter?: string | null;
}

const PolicyManagementTable: React.FC<PolicyManagementTableProps> = ({
  data,
  viewedType,
  selectedPeriodType,
  handlerFilter,
}) => {
  const filteredData = useMemo(
    () => handlerFilter ? data.filter((r) => r.handler_code === handlerFilter) : data,
    [data, handlerFilter]
  );
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState<ManualMimshakData | null>(null);
  const [historyPolicy, setHistoryPolicy] = useState<PolicyHistoryKey | null>(null);
  // The drawer (and its chunk) loads on the first history click, then stays mounted for its close animation
  const [historyOpened, setHistoryOpened] = useState(false);
  const openHistory = (policy: PolicyHistoryKey) => {
    setHistoryOpened(true);
    setHistoryPolicy(policy);
  };

  const formattedRows = useMemo(() => {
    const rows = filteredData.map(buildPolicyRow);
    // מיון דיפולטי לפי סוג מוצר (שם), ובתוך כל סוג — לפי יתרה יורדת; סוג לא ידוע בסוף
    return rows.sort((a, b) => {
      const aLabel = formatAdjustedProductType(a.adjusted_product_type);
      const bLabel = formatAdjustedProductType(b.adjusted_product_type);
      if (!aLabel !== !bLabel) return aLabel ? -1 : 1;
      return aLabel.localeCompare(bLabel, "he") || (b.funds_sum || 0) - (a.funds_sum || 0);
    });
  }, [filteredData]);

  const contactFirstNameColumn = useContactFirstNameColumn();
  // The client column only matters when the table holds more than one client's policies
  const hasMultipleContacts = useMemo(() => new Set(data.map((row) => row.contact_id)).size > 1, [data]);

  const columns: GridColDef[] = useMemo(() => {
    const baseCols: GridColDef[] = [
      ...(hasMultipleContacts ? [contactFirstNameColumn] : []),
      adjustedProductTypeColumnFullColor(),
      policyNumberWithPathTooltipColumn(),
      handlerCodeUniversalColumn(),
      { ...currencyColumn("funds_sum", "יתרה כוללת", 100, true), cellClassName: "cell-funds-sum" },
      managementFeeColumn("saving_fee", "ד.נ צבירה", PensionFeeExpenseType.FROM_SAVINGS, 100),
      managementFeeColumn("deposit_fee", "ד.נ הפקדה", PensionFeeExpenseType.FROM_DEPOSITS, 100),
      depositsWithdrawalsColumn(),
      transfersColumn(),
      productStatusChipsColumn(),
      { field: "policy_plan_or_account_type", headerName: "סוג תוכנית", width: 100 },
      isoDateColumn("agent_appointment_date", "תאריך מינוי סוכן", 100),
      { field: "policy_agent_id", headerName: "מזהה סוכן", width: 100 },
      isoDateColumn("policy_join_date", "תאריך פתיחה", 100),
      productJoinDateColumn(),
      { field: "reported_period", headerName: "תקופה מדווחת", width: 140 },
      {
        field: "history",
        headerName: "היסטוריה",
        width: 90,
        sortable: false,
        filterable: false,
        // an investment portfolio (loaded by hand) has no Mimshak history
        renderCell: (params: any) =>
          params.row.__isSummary || isInvestmentPortfolio(params.row) ? null : (
            <Tooltip title="כל הרשומות של הפוליסה">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openHistory({
                    contactId: params.row.contact_id,
                    handlerCode: params.row.handler_code,
                    policyNumber: params.row.policy_number,
                  });
                }}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
      },
    ];

    // Pre-advice has a single snapshot per policy — no transfers and no history to show
    const filteredCols = selectedPeriodType === "PRE_ADVICE"
      ? baseCols.filter((col) => col.field !== "transfer_out" && col.field !== "history")
      : baseCols;

    return filteredCols.map((col) => ({
      ...col,
      headerClassName: "mimshak-table-header",
      headerAlign: "center" as const,
    }));
  }, [selectedPeriodType, contactFirstNameColumn, hasMultipleContacts]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRowClick = (params: any) => {
    const originalRow = filteredData[params.id];
    if (isInvestmentPortfolio(originalRow)) {
      setSelectedPortfolio(originalRow as ManualMimshakData);
      return;
    }
    setSelectedProduct(originalRow.data?.products[0] || null);
  };

  const totalFundsSum = useMemo(
    () => filteredData.reduce((sum, r) => sum + (r.funds_sum || 0), 0),
    [filteredData]
  );

  const totalDepositAmount = useMemo(
    () => filteredData.reduce((sum, r) => sum + (r.deposit_amount || 0), 0),
    [filteredData]
  );

  const totalWithdrawalAmount = useMemo(
    () => filteredData.reduce((sum, r) => sum + (r.withdrawal_amount || 0), 0),
    [filteredData]
  );

  const summaryRow = useMemo(() => ({
    id: -1,
    __isSummary: true,
    funds_sum: totalFundsSum,
    contact_id: 0,
    handler_code: "",
    reported_period: "",
    product_type: "",
    raw_product_type: 0,
    policy_number: "",
    deposit_amount: totalDepositAmount,
    withdrawal_amount: totalWithdrawalAmount,
    transfer_out: 0,
    transfer_in: 0,
    transfer_out_to: "",
    agent_appointment_date: "",
    new_policy: false,
    adjusted_product_type: 0,
    policy_plan_or_account_type: "",
    policy_agent_id: "",
    policy_join_date: "",
    product: null as Product | null,
    data_access_type: 0,
    data: null as MimshakData["data"] | null,
  }), [totalFundsSum, totalDepositAmount, totalWithdrawalAmount]);

  const fundsCol: GridColDef = useMemo(() => ({
    ...columns.find((c) => c.field === "funds_sum")!,
    renderCell: (params: any) => {
      if (params.row.__isSummary) {
        return <span dir="ltr" style={{ fontWeight: 800, color: "#1565c0" }}>{formatCurrency(totalFundsSum)}</span>;
      }
      const orig = columns.find((c) => c.field === "funds_sum")?.renderCell as ((p: any) => React.ReactNode) | undefined;
      return orig ? orig(params) : params.value;
    },
  }), [columns, totalFundsSum]);

  const handlerCol: GridColDef = useMemo(() => ({
    ...columns.find((c) => c.field === "handler_code")!,
    renderCell: (params: any) => {
      if (params.row.__isSummary) {
        return <span style={{ fontWeight: 700, fontSize: 12, color: "#1565c0" }}>סה"כ{handlerFilter ? ` (${filteredData.length})` : ""}</span>;
      }
      const orig = columns.find((c) => c.field === "handler_code")?.renderCell as ((p: any) => React.ReactNode) | undefined;
      return orig ? orig(params) : params.value;
    },
  }), [columns, handlerFilter, filteredData.length]);

  const depositCol: GridColDef = useMemo(() => ({
    ...columns.find((c) => c.field === "deposit_amount")!,
    renderCell: (params: any) => {
      if (!params.row.__isSummary) {
        const orig = columns.find((c) => c.field === "deposit_amount")?.renderCell as ((p: any) => React.ReactNode) | undefined;
        return orig ? orig(params) : null;
      }
      const hasDep = totalDepositAmount !== 0;
      const hasWit = totalWithdrawalAmount !== 0;
      if (!hasDep && !hasWit) return null;
      return (
        <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
          {hasDep && <span dir="ltr" style={{ fontWeight: 700, color: "#1565c0" }}>{formatCurrency(totalDepositAmount)}</span>}
          {hasWit && <span dir="ltr" style={{ fontWeight: 700, color: "#d32f2f" }}>-{formatCurrency(totalWithdrawalAmount)}</span>}
        </Box>
      );
    },
  }), [columns, totalDepositAmount, totalWithdrawalAmount]);

  const summaryColumns: GridColDef[] = useMemo(() =>
    columns.map((col) => {
      if (col.field === "funds_sum") return fundsCol;
      if (col.field === "handler_code") return handlerCol;
      if (col.field === "deposit_amount") return depositCol;
      return col;
    }),
  [columns, fundsCol, handlerCol, depositCol]);

  return (
    <Box dir="rtl" sx={{ maxWidth: "100%", margin: "auto", padding: 2, position: "relative" }}>
      <StyledDataGrid
        rows={totalFundsSum > 0 ? [...formattedRows, summaryRow] : formattedRows}
        columns={summaryColumns}
        onCellClick={(params) => { if (!params.row.__isSummary && params.field !== "history") handleRowClick(params); }}
        maxHeight="700px"
        hideFooter={formattedRows.length <= 100}
        getRowClassName={(params) => params.row.__isSummary ? "row-summary" : ""}
        sx={{
          "& .cell-funds-sum": { bgcolor: "#e3f2fd" },
          "& .row-summary": {
            bgcolor: "#dbeafe",
            fontWeight: 800,
            borderTop: "2px solid #90caf9",
            "& .MuiDataGrid-cell": { borderBottom: "none" },
          },
        }}
      />

      {selectedProduct && (
        <InsuranceProductDrawer product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}

      {selectedPortfolio && (
        <InvestmentPortfolioDrawer
          manualMimshakData={selectedPortfolio}
          onClose={() => setSelectedPortfolio(null)}
          contactName={selectedPortfolio.data?.products?.[0]?.client?.client_name}
        />
      )}

      {historyOpened && (
        <Suspense fallback={null}>
          <PolicyHistoryDrawer
            policy={historyPolicy}
            onClose={() => setHistoryPolicy(null)}
            selectedPeriodType={selectedPeriodType}
          />
        </Suspense>
      )}
    </Box>
  );
};

export default PolicyManagementTable;
